"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { Hourglass, LogOut, Menu, User } from "lucide-react";
import { useAuthStore } from "@/lib/stores/auth-store";
import { useSidebarStore } from "@/lib/stores/sidebar-store";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/theme-toggle";
import { NotificationBell } from "@/components/dashboard/notification-bell";
import { LocaleSwitcher } from "@/components/i18n/locale-switcher";

export function TopBar() {
  const t = useTranslations();
  const pathname = usePathname();
  const { user, logout } = useAuthStore();
  const { toggle } = useSidebarStore();
  const [now] = useState(() => Date.now());

  // Ordered list: more specific prefixes first.
  const titles: Array<[string, string]> = [
    ["/signals/manual", t("dashboard.manualSignal.title")],
    ["/signals", t("dashboard.sidebar.signals")],
    ["/analytics", t("dashboard.sidebar.analytics")],
    ["/flows", t("dashboard.sidebar.flows")],
    ["/sources", t("dashboard.sidebar.sources")],
    ["/destinations", t("dashboard.sidebar.destinations")],
    ["/settings/billing", t("dashboard.sidebar.billingUsage")],
    ["/settings", t("dashboard.sidebar.settings")],
    ["/support", t("dashboard.sidebar.support")],
    ["/usage", t("dashboard.usagePage.title")],
    ["/notifications", t("dashboard.notifications.title")],
    ["/overview", t("dashboard.sidebar.home")],
  ];
  const pageTitle =
    titles.find(
      ([prefix]) => pathname === prefix || pathname.startsWith(prefix + "/")
    )?.[1] ?? "";

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  const trialDaysLeft = user?.trialEndsAt
    ? Math.max(
        0,
        Math.ceil(
          (new Date(user.trialEndsAt).getTime() - now) / 86400000
        )
      )
    : 0;

  return (
    <header
      className="fixed start-0 end-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/45 px-4 backdrop-blur-lg lg:start-64 lg:px-6"
      style={{ top: "var(--imp-banner-h, 0px)" }}
    >
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={toggle}
        aria-label={t("dashboard.topBar.toggleMenu")}
      >
        <Menu className="h-5 w-5" />
      </Button>

      <h1 className="font-heading text-lg font-bold tracking-tight">{pageTitle}</h1>

      <div className="ms-auto flex items-center gap-2">
        {user?.isTrialActive && (
          <Link
            href="/settings/billing"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-full border border-warning/30 bg-warning/10 px-3 py-1 text-xs font-semibold text-warning sm:inline-flex"
          >
            <Hourglass className="h-3 w-3" aria-hidden="true" />
            {t("dashboard.topBar.trialPill", { days: trialDaysLeft })}
          </Link>
        )}
        <NotificationBell />
        <ThemeToggle />
        <LocaleSwitcher />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2" aria-label={t("dashboard.topBar.userMenu")}>
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-gradient-to-br from-primary/30 to-secondary/30 text-xs font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-sm sm:inline">
                {user?.name || user?.email}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="text-xs text-muted-foreground">
              {user?.email}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/settings" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {t("dashboard.topBar.settings")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={logout}
              className="flex items-center gap-2 text-destructive"
            >
              <LogOut className="h-4 w-4" />
              {t("dashboard.topBar.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
